import { useState, type FormEvent } from 'react';
import {
  useAdminGroups, useSaveAdminGroup, useUpdateAdminGroup, useDeleteAdminGroup,
} from '../../lib/api/hooks-extension.js';
import { TableSkeleton, Empty, ErrorBanner, SectionHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';

/**
 * Departments: the groups an account's extensions are filed under, for spend
 * reports and dialling restrictions (`admin_groups` in the API).
 *
 * The API could create one and no screen could, so an extension's Department
 * offered nothing to choose (owner, 15 September 2026). They are listed here,
 * and a new one can also be made from the extension itself.
 */

interface Department { id: string; name: string; code: string | null; extension_count?: number }

export function DepartmentsTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const groups = useAdminGroups(tenantId);
  const remove = useDeleteAdminGroup(tenantId);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const mayWrite = can('extension:write');
  const rows = groups.data?.data ?? [];

  return (
    <>
      <SectionHead title="Departments" sub="Extensions grouped for spend reports and dialling restrictions."
        action={mayWrite && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
            onClick={() => { setAdding((v) => !v); setEditing(null); }}>
            <Icon name={adding ? 'close' : 'plus'} size={13} />
            {adding ? 'Cancel' : 'Add department'}
          </button>
        )} />

      {groups.error && <ErrorBanner error={groups.error} />}
      {adding && <AddDepartment tenantId={tenantId} onDone={() => setAdding(false)} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr><th>Name</th><th>Code</th><th className="arc-td-right">Extensions</th><th /></tr>
            </thead>
            {groups.isLoading ? <TableSkeleton cols={4} /> : (
              <tbody>
                {rows.map((g) => editing === g.id
                  ? <EditDepartment key={g.id} tenantId={tenantId} group={g} onDone={() => setEditing(null)} />
                  : (
                    <tr key={g.id}>
                      <td style={{ fontWeight: 550 }}>{g.name}</td>
                      <td className="arc-mono arc-muted">{g.code ?? ''}</td>
                      <td className="arc-td-right arc-num">{g.extension_count ?? 0}</td>
                      <td className="arc-td-right">
                        {mayWrite && (
                          <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              onClick={() => { setEditing(g.id); setAdding(false); }}>
                              Rename
                            </button>
                            {/* Refused while extensions are in it, and the refusal says how many. */}
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              disabled={remove.isPending}
                              onClick={() => { if (confirm(`Delete ${g.name}?`)) remove.mutate(g.id); }}>
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            )}
          </table>
        </div>
        {!groups.isLoading && rows.length === 0 && (
          <Empty icon="users" title="No departments"
            hint="Add one, then choose it on each extension's Details." />
        )}
      </div>
    </>
  );
}

function AddDepartment({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const save = useSaveAdminGroup(tenantId);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({ name: name.trim(), ...(code.trim() ? { code: code.trim() } : {}) }, { onSuccess: onDone });
  };

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr) auto', gap: 12, alignItems: 'start',
      }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dept-name">Name</label>
          <input id="dept-name" className="arc-input" required autoFocus maxLength={80}
            value={name} onChange={(e) => setName(e.target.value)} placeholder="Sales" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dept-code">Code</label>
          <input id="dept-code" className="arc-input arc-mono" maxLength={24}
            value={code} onChange={(e) => setCode(e.target.value)} placeholder="optional" />
          <div className="arc-hint">A finance code, if reports need one.</div>
        </div>
        <div style={{ paddingTop: 23 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </form>
  );
}

function EditDepartment({ tenantId, group, onDone }: {
  tenantId: string; group: Department; onDone: () => void;
}) {
  const update = useUpdateAdminGroup(tenantId);
  const [name, setName] = useState(group.name);
  const [code, setCode] = useState(group.code ?? '');

  const submit = () => {
    if (!name.trim()) return;
    update.mutate({ id: group.id, name: name.trim(), code: code.trim() || null }, { onSuccess: onDone });
  };
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
    if (e.key === 'Escape') onDone();
  };

  return (
    <tr>
      <td>
        <input className="arc-input" aria-label="Department name" autoFocus maxLength={80}
          value={name} onChange={(e) => setName(e.target.value)} onKeyDown={keys} />
      </td>
      <td>
        <input className="arc-input arc-mono" aria-label="Department code" maxLength={24} placeholder="optional"
          value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={keys} />
      </td>
      <td className="arc-td-right arc-num">{group.extension_count ?? 0}</td>
      <td className="arc-td-right">
        <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 6 }}>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onDone}>Cancel</button>
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
            disabled={update.isPending || !name.trim()} onClick={submit}>
            Save
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * An extension's department, with a new one made on the spot: somebody filling
 * in a seat should not have to leave it to create the department it belongs in.
 */
export function DepartmentSelect({ id, tenantId, value, onChange }: {
  id: string; tenantId: string; value: string; onChange: (id: string) => void;
}) {
  const can = useSession((s) => s.can);
  const groups = useAdminGroups(tenantId);
  const save = useSaveAdminGroup(tenantId);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  const add = () => {
    if (!name.trim()) return;
    save.mutate({ name: name.trim() }, {
      onSuccess: (created) => { onChange(created.id); setNaming(false); setName(''); },
    });
  };

  if (naming) {
    return (
      <>
        <div className="arc-row" style={{ gap: 6 }}>
          <input id={id} className="arc-input" autoFocus maxLength={80} placeholder="New department"
            value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); add(); }
              if (e.key === 'Escape') setNaming(false);
            }} />
          <button type="button" className="arc-btn arc-btn-sm" disabled={save.isPending || !name.trim()}
            onClick={add}>
            Add
          </button>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setNaming(false)}>
            Cancel
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="arc-row" style={{ gap: 6 }}>
      <select id={id} className="arc-select" style={{ flex: 1 }} value={value}
        onChange={(e) => onChange(e.target.value)}>
        <option value="">— none —</option>
        {groups.data?.data.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
      </select>
      {can('extension:write') && (
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setNaming(true)}>
          <Icon name="plus" size={12} /> New
        </button>
      )}
    </div>
  );
}
